import { AiCompanionReviewModeEnum } from "@simbelapp/order-sdk";
import {
  CopilotAttendanceStepEnum,
  CopilotReviewStepEnum,
  CopilotStepStatusEnum,
} from "~/utils/enums/copilot/workflow.enums";
import type { ICopilotAnalysis } from "~/utils/interfaces/copilot-blocks-interfaces";

/** How the design heads a workflow it recognises, whatever the backend called it. */
const ANALYSIS_TITLE = "Traitement de la demande…";

export const ATTENDANCE_STEP_LABELS: Record<CopilotAttendanceStepEnum, string> = {
  [CopilotAttendanceStepEnum.UNDERSTAND_REQUEST]: "Demande identifiée : déclaration des présences",
  [CopilotAttendanceStepEnum.ANALYZE_SIGN_IN_SHEET]: "Analyse de la feuille d'émargement",
  [CopilotAttendanceStepEnum.FIND_SESSION]: "Recherche de la session",
};

const CREATE_STEP_LABELS: Record<CopilotReviewStepEnum, string> = {
  [CopilotReviewStepEnum.READ_PROGRAM]: "Demande identifiée : ajout d'une formation au catalogue",
  [CopilotReviewStepEnum.EXTRACT_PROGRAM]: "Analyse du programme",
  [CopilotReviewStepEnum.MATCH_TRAINING]: "Aucun doublon détecté dans le catalogue",
  [CopilotReviewStepEnum.BUILD_REVIEW]: "Préparation de la fiche formation",
};

const UPDATE_STEP_LABELS: Record<CopilotReviewStepEnum, string> = {
  [CopilotReviewStepEnum.READ_PROGRAM]: "Demande identifiée : mise à jour de la formation",
  [CopilotReviewStepEnum.EXTRACT_PROGRAM]: "Analyse du programme",
  [CopilotReviewStepEnum.MATCH_TRAINING]: "Comparaison avec la fiche existante",
  [CopilotReviewStepEnum.BUILD_REVIEW]: "Préparation des modifications",
};

export function resolveStepLabels(
  analysis: ICopilotAnalysis,
  mode: AiCompanionReviewModeEnum | null,
): ICopilotAnalysis {
  const reviewLabels = mode === AiCompanionReviewModeEnum.Update ? UPDATE_STEP_LABELS : CREATE_STEP_LABELS;

  // `Object.hasOwn` rather than `in`: the step id comes off the stream, and `in` would match an
  // inherited key — a step named "toString" would resolve to a function and render as one.
  const resolveLabel = (id: string): string | null => {
    if (Object.hasOwn(ATTENDANCE_STEP_LABELS, id)) {
      return ATTENDANCE_STEP_LABELS[id as CopilotAttendanceStepEnum];
    }

    if (Object.hasOwn(reviewLabels, id)) {
      return reviewLabels[id as CopilotReviewStepEnum];
    }

    return null;
  };

  const hasMappedSteps = analysis.steps.some((step) => resolveLabel(step.step ?? step.label) !== null);
  if (!hasMappedSteps) return analysis;

  return {
    ...analysis,
    title: ANALYSIS_TITLE,
    steps: analysis.steps.map((step) => {
      const stepId = step.step ?? step.label;
      const label = resolveLabel(stepId);
      if (label === null) return step;

      const isRunningSheetAnalysis =
        stepId === CopilotAttendanceStepEnum.ANALYZE_SIGN_IN_SHEET && step.status === CopilotStepStatusEnum.RUNNING;

      return {
        ...step,
        label: isRunningSheetAnalysis ? `${label} — cela peut prendre un moment` : label,
      };
    }),
  };
}
